<template>
  <div class="class-list-iuss-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Student's List</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ totalStudents }} Students
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" type="button" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" type="button" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      <p class="mt-3 font-weight-bold">Loading student list...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else-if="!reportGroups.length" class="report-page text-center py-5">
      <h5 class="text-secondary">No students found for the selected criteria</h5>
    </div>

    <div v-else class="report-page">
      <header class="report-main-header text-center">
        <h1 class="org-name">{{ organizationName }}</h1>
        <h2 class="campus-title">{{ campusBranch || campusName }}</h2>
        <h3 class="report-title">STUDENT'S LIST ({{ statusLabel }})</h3>
      </header>

      <section
        v-for="(group, groupIndex) in reportGroups"
        :key="'group-' + groupIndex"
        class="class-section"
      >
        <table class="class-list-table" :class="{ 'has-fee-col': isRegistered }">
          <thead>
            <tr class="row-campus">
              <th :colspan="tableColspan">{{ campusBranch || campusName }}</th>
            </tr>
            <tr class="row-class">
              <th :colspan="tableColspan">{{ group.groupTitle }}</th>
            </tr>
            <tr class="row-columns">
              <th class="col-sno">S.No</th>
              <th class="col-gr">GR #</th>
              <th class="col-name">Name</th>
              <th class="col-father">Father Name</th>
              <th class="col-dob">Date of Birth</th>
              <th class="col-contact">Contact Details</th>
              <th v-if="isRegistered" class="col-fee">Fee Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(student, index) in group.students"
              :key="'student-' + groupIndex + '-' + index"
            >
              <td class="cell-sno">{{ index + 1 }}</td>
              <td class="cell-gr">{{ student.grNo || "—" }}</td>
              <td class="cell-name">{{ formatName(student.name) }}</td>
              <td class="cell-father">{{ formatName(student.fatherName) }}</td>
              <td class="cell-dob">{{ formatDob(student) }}</td>
              <td class="cell-contact">{{ formatContact(student) }}</td>
              <td v-if="isRegistered" class="cell-fee">
                <template v-if="getStudentFees(student).length">
                  <div
                    v-for="(fee, feeIndex) in getStudentFees(student)"
                    :key="'fee-' + groupIndex + '-' + index + '-' + feeIndex"
                    class="fee-line"
                  >
                    {{ formatSingleFee(fee) }}
                  </div>
                </template>
                <template v-else>
                  {{ formatFeeStatus(student) }}
                </template>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="row-summary">
              <td :colspan="tableColspan">{{ group.summary }}</td>
            </tr>
          </tfoot>
        </table>
      </section>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

function getClassSequence(className) {
  const cls = (className || "").toString().trim().toUpperCase();

  if (cls.includes("PRE-ADMISSION") || cls.includes("PRE ADMISSION")) return 0;
  if (cls.includes("PRE NURSERY") || cls.includes("PRE-NURSERY")) return 1;
  if (cls.includes("NURSERY")) return 2;
  if (cls.includes("KINDER") || cls.includes("KG")) return 3;

  const tokens = cls.replace(/[^A-Z0-9]/g, " ").split(/\s+/);
  if (tokens.includes("XII") || tokens.includes("12")) return 15;
  if (tokens.includes("XI") || tokens.includes("11")) return 14;
  if (tokens.includes("X") || tokens.includes("10")) return 13;
  if (tokens.includes("IX") || tokens.includes("9")) return 12;
  if (tokens.includes("VIII") || tokens.includes("8")) return 11;
  if (tokens.includes("VII") || tokens.includes("7")) return 10;
  if (tokens.includes("VI") || tokens.includes("6")) return 9;
  if (tokens.includes("V") || tokens.includes("5")) return 8;
  if (tokens.includes("IV") || tokens.includes("4")) return 7;
  if (tokens.includes("III") || tokens.includes("3")) return 6;
  if (tokens.includes("II") || tokens.includes("2")) return 5;
  if (tokens.includes("I") || tokens.includes("1")) return 4;

  return 999;
}

export default {
  name: "ClassListIUSSPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportGroups: [],
      organizationName: "IU School System",
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      classID: 0,
      sectionID: 0,
      status: "present",
      feePaymentStatus: "",
      totalStudents: 0,
      autoExport: false,
      exporting: false,
    };
  },
  computed: {
    isRegistered() {
      return String(this.status || "").toLowerCase() === "registered";
    },
    tableColspan() {
      return this.isRegistered ? 7 : 6;
    },
    statusLabel() {
      const value = String(this.status || "present").toLowerCase();
      const labels = {
        present: "Present",
        registered: "Registered",
        left: "Left",
        suspended: "Suspended",
      };
      return labels[value] || value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = Number(query.cID || this.$store.state.userData.cId || 0);
      this.classID = Number(query.classID || 0);
      this.sectionID = Number(query.sectionID || 0);
      this.status = query.status || "present";
      this.feePaymentStatus = query.feePaymentStatus || "";
      this.autoExport = ["1", "true", true].includes(query.export);

      if (!this.db) {
        this.error = "System configuration error: Database context missing.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          `${this.$store.state.domain}campuses`,
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.organizationName =
              match.organizationName || match.name || "IU School System";
            this.campusName = match.name || "MY SKOOL";
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const currentBranch =
          (this.$store.state.branches || []).find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.organizationName =
            currentBranch.organizationName ||
            currentBranch.name ||
            "IU School System";
          this.campusName =
            currentBranch.organizationName || currentBranch.branch || "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const params = {
          db: this.db,
          cID: this.cID,
          classID: this.classID || 0,
          sectionID: this.sectionID || 0,
          departmentID: 0,
          status: this.status || "present",
        };

        if (
          this.feePaymentStatus &&
          String(this.status).toLowerCase() === "registered"
        ) {
          params.feePaymentStatus = this.feePaymentStatus;
        }

        const response = await axios.get(
          `${this.$store.state.domain}Students/ClassListReport`,
          {
            params,
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        const payload = response.data || {};
        if (payload.status && payload.status !== "success") {
          this.error = payload.message || "Failed to load class list report.";
          this.reportGroups = [];
          this.totalStudents = 0;
          return;
        }

        this.reportGroups = this.buildSectionGroups(payload.groups || []);
        this.totalStudents =
          Number(payload.totalCount) ||
          this.reportGroups.reduce(
            (sum, group) => sum + (group.students || []).length,
            0
          );

        if (this.autoExport && !this.error) {
          this.$nextTick(() => this.exportExcel(true));
        }
      } catch (err) {
        console.error("Error fetching class list report:", err);
        this.error = "Network error or service unavailable.";
        this.reportGroups = [];
        this.totalStudents = 0;
      } finally {
        this.loading = false;
      }
    },
    buildSectionGroups(groups) {
      const students = [];
      (Array.isArray(groups) ? groups : []).forEach((group) => {
        (group.students || []).forEach((student) => {
          students.push({
            ...student,
            className: student.className || group.className || "",
            sectionName: student.sectionName || "",
          });
        });
      });

      const grouped = new Map();
      students.forEach((student) => {
        const className = (student.className || "Unassigned Class").trim();
        const sectionName = (student.sectionName || "").trim();
        const groupTitle = sectionName
          ? `${className} - ${sectionName}`
          : className;
        const key = groupTitle.toUpperCase();

        if (!grouped.has(key)) {
          grouped.set(key, {
            groupTitle,
            className,
            sectionName,
            students: [],
            maleCount: 0,
            femaleCount: 0,
          });
        }

        const bucket = grouped.get(key);
        bucket.students.push(student);

        const gender = String(student.gender || "").toLowerCase();
        if (
          gender === "female" ||
          gender === "f" ||
          gender === "girl" ||
          gender === "girls"
        ) {
          bucket.femaleCount += 1;
        } else {
          bucket.maleCount += 1;
        }
      });

      return [...grouped.values()]
        .map((group) => {
          const count = group.students.length;
          group.students.sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), undefined, {
              sensitivity: "base",
            })
          );
          group.summary = `${count} students in ${group.groupTitle} | ${group.maleCount} M | ${group.femaleCount} F`;
          return group;
        })
        .sort((a, b) => {
          const seqA = getClassSequence(a.className);
          const seqB = getClassSequence(b.className);
          if (seqA !== seqB) return seqA - seqB;

          const classCmp = a.className.localeCompare(b.className, undefined, {
            sensitivity: "base",
          });
          if (classCmp !== 0) return classCmp;

          if (!a.sectionName) return 1;
          if (!b.sectionName) return -1;
          return a.sectionName.localeCompare(b.sectionName, undefined, {
            sensitivity: "base",
          });
        });
    },
    formatName(value) {
      return String(value || "—")
        .trim()
        .toUpperCase();
    },
    formatDob(student) {
      const raw =
        student.dob ||
        student.dateOfBirth ||
        student.date_of_birth ||
        student.DOB ||
        "";
      if (!raw) return "—";

      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return String(raw);

      return date
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "-");
    },
    formatContact(student) {
      if (student.contactDetails) {
        return String(student.contactDetails)
          .split(/\r?\n/)
          .map((part) => part.trim())
          .filter(Boolean)
          .join(", ");
      }

      const contacts = [
        student.contact_father,
        student.contact_mother,
        student.contact_student,
        student.contact_home,
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean);

      return contacts.length ? contacts.join(", ") : "—";
    },
    formatAmount(value) {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return "0";
      return amount.toLocaleString("en-PK");
    },
    getStudentFees(student) {
      return Array.isArray(student?.fees)
        ? student.fees.filter((fee) => fee && (fee.feeTypeName || fee.feesType))
        : [];
    },
    formatSingleFee(fee) {
      const name = String(fee.feeTypeName || "Fee").trim();
      const status = String(fee.status || "").toLowerCase();
      const statusLabel =
        status === "paid"
          ? "Paid"
          : status === "unpaid"
          ? "Unpaid"
          : status
          ? status.charAt(0).toUpperCase() + status.slice(1)
          : "—";

      const amount =
        status === "paid"
          ? Number(fee.rcvd ?? fee.netFee ?? fee.actualFee ?? 0)
          : Number(fee.balance ?? fee.netFee ?? fee.actualFee ?? 0);

      return `${name}: ${statusLabel} | ${this.formatAmount(amount)}`;
    },
    formatFeeDetails(student) {
      const fees = this.getStudentFees(student);
      if (fees.length) {
        return fees.map((fee) => this.formatSingleFee(fee)).join("\n");
      }
      return this.formatFeeStatus(student);
    },
    formatFeeStatus(student) {
      const paymentStatus = String(
        student.feePaymentStatus || ""
      ).toLowerCase();

      if (paymentStatus === "paid") {
        const received = this.formatAmount(student.latestFeeRcvd);
        return `Paid | Received: ${received}`;
      }

      if (paymentStatus === "unpaid") {
        const balance = this.formatAmount(student.latestFeeBalance);
        return `Unpaid | Fee: ${balance}`;
      }

      if (student.latestFeeBalance != null && Number(student.latestFeeBalance) > 0) {
        return `Fee: ${this.formatAmount(student.latestFeeBalance)}`;
      }

      if (student.latestFeeRcvd != null && Number(student.latestFeeRcvd) > 0) {
        return `Received: ${this.formatAmount(student.latestFeeRcvd)}`;
      }

      return paymentStatus
        ? paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1)
        : "—";
    },
    buildExportRows() {
      const rows = [
        [
          "S.No",
          "Campus",
          "Class / Section",
          "GR #",
          "Name",
          "Father Name",
          "Date of Birth",
          "Contact Details",
          ...(this.isRegistered ? ["Fee Status"] : []),
        ],
      ];

      this.reportGroups.forEach((group) => {
        group.students.forEach((student, index) => {
          rows.push([
            index + 1,
            this.campusBranch || this.campusName,
            group.groupTitle,
            student.grNo || "",
            this.formatName(student.name),
            this.formatName(student.fatherName),
            this.formatDob(student) === "—" ? "" : this.formatDob(student),
            this.formatContact(student) === "—" ? "" : this.formatContact(student),
            ...(this.isRegistered ? [this.formatFeeDetails(student)] : []),
          ]);
        });
      });

      return rows;
    },
    async exportExcel(isAuto = false) {
      if (this.exporting || !this.reportGroups.length) return;
      this.exporting = true;

      try {
        const XLSX = await import("xlsx");
        const rows = this.buildExportRows();
        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Student List");

        const statusLabel = String(this.status || "present");
        const fileName = `Student_List_${statusLabel}_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        if (isAuto) {
          setTimeout(() => window.close(), 400);
        }
      } catch (err) {
        console.error("Class list export error:", err);
        this.error = "Failed to export report.";
      } finally {
        this.exporting = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.class-list-iuss-report {
  --ink: #000;
  --border: #000;
  --header-bg: #fff;
  --class-bg: #fff;
  --summary-bg: #fff;

  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  color: var(--ink);
  font-family: "Outfit", "Inter", "Segoe UI", Calibri, Arial, sans-serif;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: #fff;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: #fff;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #fff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-main-header {
  margin-bottom: 1.35rem;
  padding-bottom: 0;

  .org-name,
  .campus-title,
  .report-title {
    margin: 0;
    font-weight: 700;
    text-transform: uppercase;
    color: #000;
    line-height: 1.25;
  }

  .org-name {
    font-size: 1.55rem;
    letter-spacing: 0.04em;
  }

  .campus-title {
    font-size: 1.2rem;
    margin-top: 0.2rem;
    color: #000;
  }

  .report-title {
    font-size: 1rem;
    margin-top: 0.45rem;
    letter-spacing: 0.12em;
    color: #000;
  }
}

.class-section {
  margin-bottom: 1.25rem;
  page-break-inside: auto;
  break-inside: auto;

  &:last-child {
    margin-bottom: 0;
  }
}

.class-list-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 0.84rem;
  border: 1px solid var(--border);
  page-break-inside: auto;
  break-inside: auto;

  &.has-fee-col {
    .col-name,
    .col-father {
      width: 17%;
    }

    .col-contact {
      width: 20%;
    }

    .col-fee {
      width: 18%;
    }
  }

  thead {
    display: table-header-group;
  }

  tfoot {
    display: table-footer-group;
  }

  tbody tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  th,
  td {
    border: 1px solid #000;
    padding: 0.4rem 0.55rem;
    vertical-align: middle;
    word-break: break-word;
    color: #000;
    font-weight: 600;
  }

  thead th {
    border-color: #000;
  }

  .row-campus th {
    text-align: center;
    font-size: 0.9rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #000;
    background: var(--header-bg);
    padding: 0.5rem 0.55rem;
    border-bottom: 1px solid #000;
  }

  .row-class th {
    text-align: center;
    font-size: 0.95rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #000;
    background: var(--class-bg);
    padding: 0.55rem 0.55rem;
    border-bottom: 1px solid #000;
  }

  .row-columns th {
    text-align: center;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #000;
    background: #fff;
    padding: 0.45rem 0.4rem;
    border-bottom: 1px solid #000;
  }

  .col-sno {
    width: 6%;
  }

  .col-gr {
    width: 10%;
  }

  .col-name,
  .col-father {
    width: 21%;
  }

  .col-dob {
    width: 12%;
  }

  .col-contact {
    width: 28%;
  }

  tbody td {
    line-height: 1.35;
    font-size: 0.83rem;
    font-weight: 600;
    color: #000;
  }

  .cell-sno {
    text-align: center;
    font-weight: 600;
    color: #000;
    font-variant-numeric: tabular-nums;
  }

  .cell-gr {
    text-align: center;
    font-weight: 600;
    color: #000;
    font-variant-numeric: tabular-nums;
  }

  .cell-name,
  .cell-father {
    text-align: left;
    font-weight: 600;
    color: #000;
    letter-spacing: 0.01em;
  }

  .cell-dob {
    text-align: center;
    color: #000;
    font-weight: 600;
    white-space: nowrap;
  }

  .cell-contact {
    text-align: left;
    color: #000;
    font-weight: 600;
    font-size: 0.83rem;
    line-height: 1.4;
  }

  .cell-fee {
    text-align: left;
    color: #000;
    font-weight: 600;
    font-size: 0.78rem;
    line-height: 1.35;
    white-space: normal;
  }

  .fee-line {
    &:not(:last-child) {
      margin-bottom: 0.2rem;
    }
  }

  .row-summary td {
    text-align: right;
    font-weight: 700;
    font-size: 0.82rem;
    color: #000;
    background: var(--summary-bg);
    border-top: 1px solid #000;
    border-bottom: 1px solid #000;
    padding: 0.5rem 0.65rem;
    letter-spacing: 0.01em;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: #fff !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .class-list-iuss-report {
    background: #fff !important;
    padding: 0 !important;
    min-height: auto;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    background: #fff !important;
  }

  .report-main-header {
    margin-bottom: 0.75rem;
    padding-bottom: 0;
    border-bottom: none !important;
    page-break-after: avoid;
    break-after: avoid;
  }

  .class-section {
    margin-bottom: 0.85rem;
    page-break-inside: auto;
    break-inside: auto;
    page-break-before: auto;
    break-before: auto;
  }

  .class-list-table {
    font-size: 9.5pt;
    page-break-inside: auto;
    break-inside: auto;
    border: 1px solid #000 !important;

    thead tr,
    tfoot tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    tbody tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    th,
    td {
      padding: 0.28rem 0.4rem;
      background: #fff !important;
      color: #000 !important;
      border: 1px solid #000 !important;
      font-weight: 600 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody td {
      font-size: 9.5pt;
    }

    .cell-dob,
    .cell-contact,
    .cell-fee {
      color: #000 !important;
      font-weight: 600 !important;
      font-size: 9pt;
    }
  }

  @page {
    size: A4 portrait;
    margin: 10mm 12mm;
  }
}
</style>
